import { useEffect, useState } from 'react';
import { Box, Button, Chip, Stack, Switch, TextField, Typography } from '@mui/material';
import ShoppingBasketRounded from '@mui/icons-material/ShoppingBasketRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import StarBorderRounded from '@mui/icons-material/StarBorderRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import LocalDrinkRounded from '@mui/icons-material/LocalDrinkRounded';
import BakeryDiningRounded from '@mui/icons-material/BakeryDiningRounded';
import EmojiNatureRounded from '@mui/icons-material/EmojiNatureRounded';
import GrainRounded from '@mui/icons-material/GrainRounded';
import SetMealRounded from '@mui/icons-material/SetMealRounded';
import HomeRepairServiceRounded from '@mui/icons-material/HomeRepairServiceRounded';
import LocalCafeRounded from '@mui/icons-material/LocalCafeRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import { createGroceryItem, updateGroceryItem, GroceryItemRow, GroceryItemPayload } from '../../services/api';
import FormDialogShell, { gradientButtonSx } from '../ui/FormDialogShell';

const COLORS: [string, string] = ['#FF9800', '#E65100'];

export const CATEGORIES: { name: string; icon: React.ComponentType<any>; color: string }[] = [
  { name: 'Dairy', icon: LocalDrinkRounded, color: '#0EA5E9' },
  { name: 'Bakery', icon: BakeryDiningRounded, color: '#D97706' },
  { name: 'Vegetables', icon: EmojiNatureRounded, color: '#16A34A' },
  { name: 'Grains', icon: GrainRounded, color: '#CA8A04' },
  { name: 'Non-veg', icon: SetMealRounded, color: '#DC2626' },
  { name: 'Essentials', icon: HomeRepairServiceRounded, color: '#6366F1' },
  { name: 'Beverages', icon: LocalCafeRounded, color: '#8B5CF6' },
  { name: 'Others', icon: MoreHorizRounded, color: '#64748B' },
];

const QUICK_QTY = ['1', '2', '500 g', '1 kg', '1 litre', '1 pack', '1 dozen'];

const labelSx = {
  fontWeight: 800, fontSize: 12.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', mb: 0.75,
} as const;

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: GroceryItemRow | null;
};

const GroceryItemFormDialog = ({ open, onClose, onSaved, editing }: Props) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Others');
  const [quantity, setQuantity] = useState('');
  const [favourite, setFavourite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; quantity?: string }>({});

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setCategory(editing.category);
      setQuantity(editing.quantity || '');
      setFavourite(editing.favourite);
    } else {
      setName(''); setCategory('Others'); setQuantity(''); setFavourite(false);
    }
    setError('');
    setFieldErrors({});
  }, [open, editing]);

  const handleSave = async () => {
    const errs: { name?: string; quantity?: string } = {};
    if (!name.trim()) errs.name = 'Item name is required';
    else if (name.trim().length > 80) errs.name = 'Item name must be 80 characters or fewer';
    if (quantity.trim().length > 40) errs.quantity = 'Quantity must be 40 characters or fewer';
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    const payload: GroceryItemPayload = { name: name.trim(), category, quantity: quantity.trim(), favourite };
    setSaving(true);
    try {
      if (editing) await updateGroceryItem(editing._id, payload);
      else await createGroceryItem(payload);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const selected = CATEGORIES.find((c) => c.name === category) || CATEGORIES[CATEGORIES.length - 1];
  const accent = selected.color;
  const SelectedIcon = selected.icon;

  return (
    <FormDialogShell
      open={open}
      onClose={onClose}
      colors={COLORS}
      maxWidth="md"
      icon={editing ? <EditRounded /> : <ShoppingBasketRounded />}
      title={editing ? 'Edit Item' : 'Add Item'}
      subtitle={editing ? 'Update this grocery item' : 'Add something to your shopping list'}
      footer={
        <>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={onClose}
            sx={{ borderRadius: '999px', px: 2.5, border: '2px solid', borderColor: 'divider', color: 'text.primary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            startIcon={<SendRounded fontSize="small" />}
            sx={gradientButtonSx(COLORS)}
          >
            {saving ? 'Saving…' : editing ? 'Update Item' : 'Add Item'}
          </Button>
        </>
      }
    >
      {error && (
        <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700, mb: 2 }}>{error}</Typography>
      )}
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
        {/* ===== Left: details ===== */}
        <Stack spacing={2.25}>
          <Box>
            <Typography sx={labelSx}>Item name</Typography>
            <TextField
              autoFocus
              fullWidth
              placeholder="e.g. Fresh milk"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); setFieldErrors((p) => ({ ...p, name: undefined })); }}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
              InputProps={{
                sx: {
                  fontSize: 19, fontWeight: 800, borderRadius: '16px', bgcolor: 'background.paper',
                  '& fieldset': { borderWidth: 2, borderColor: `${accent}55` },
                  '&:hover fieldset': { borderColor: `${accent}99` },
                  '&.Mui-focused fieldset': { borderColor: accent },
                  '&.Mui-focused': { boxShadow: `0 0 0 5px ${accent}22` },
                },
              }}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>Quantity</Typography>
            <TextField
              fullWidth
              size="small"
              value={quantity}
              onChange={(e) => { setQuantity(e.target.value); setFieldErrors((p) => ({ ...p, quantity: undefined })); }}
              error={!!fieldErrors.quantity}
              helperText={fieldErrors.quantity}
              placeholder="e.g. 1 litre, 500 g"
              InputProps={{ sx: { borderRadius: '14px', bgcolor: 'background.paper', fontWeight: 600 } }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 1 }}>
              {QUICK_QTY.map((q) => {
                const active = quantity === q;
                return (
                  <Chip
                    key={q}
                    label={q}
                    size="small"
                    clickable
                    onClick={() => setQuantity(active ? '' : q)}
                    sx={{
                      fontWeight: 700,
                      color: active ? '#fff' : '#0369A1',
                      background: active ? 'linear-gradient(135deg,#0EA5E9,#38BDF8)' : '#0EA5E914',
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          <Box
            onClick={() => setFavourite((f) => !f)}
            role="switch"
            aria-checked={favourite}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFavourite((f) => !f); } }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1, cursor: 'pointer',
              borderRadius: '16px', border: '2px solid', borderColor: favourite ? '#F59E0B' : 'divider',
              background: favourite ? 'linear-gradient(135deg, #FEF3C7, #FDE68A)' : 'transparent',
              transition: 'all .2s ease',
            }}
          >
            <Box sx={{ color: favourite ? '#F59E0B' : 'text.disabled', display: 'grid' }}>
              {favourite ? <StarRounded /> : <StarBorderRounded />}
            </Box>
            <Typography sx={{ fontWeight: 800, flex: 1, color: favourite ? '#92400E' : 'text.primary' }}>
              Mark as favourite
            </Typography>
            <Switch checked={favourite} onChange={(e) => setFavourite(e.target.checked)} onClick={(e) => e.stopPropagation()} color="warning" />
          </Box>
        </Stack>

        {/* ===== Right: category + live preview ===== */}
        <Stack spacing={2.25}>
          <Box>
            <Typography sx={labelSx}>Category</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.9 }}>
              {CATEGORIES.map((c) => {
                const active = c.name === category;
                const Icon = c.icon;
                return (
                  <Box
                    key={c.name}
                    component="button"
                    type="button"
                    onClick={() => setCategory(c.name)}
                    sx={{
                      cursor: 'pointer', border: '2px solid', borderColor: active ? c.color : `${c.color}40`,
                      borderRadius: '14px', py: 0.9, px: 0.25,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.35,
                      fontWeight: 800, fontSize: 11.5, fontFamily: 'inherit', minWidth: 0,
                      color: active ? '#fff' : c.color,
                      background: active ? `linear-gradient(135deg, ${c.color}, ${c.color}B3)` : `${c.color}12`,
                      boxShadow: active ? `0 8px 16px ${c.color}55` : 'none',
                      transition: 'all .15s ease',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                    <Box component="span" sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box>
            <Typography sx={labelSx}>Preview</Typography>
            <Box
              sx={{
                position: 'relative', overflow: 'hidden', borderRadius: '20px', color: '#fff', p: 1.75,
                display: 'flex', alignItems: 'center', gap: 1.5,
                background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 55%, #F6A938 140%)`,
                boxShadow: `0 14px 28px ${accent}44`,
                transition: 'background .3s ease, box-shadow .3s ease',
                '&::before': {
                  content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                  background: 'linear-gradient(135deg, rgba(255,255,255,.38), rgba(255,255,255,.06) 45%, rgba(255,255,255,0) 46%, rgba(0,0,0,.12))',
                },
              }}
            >
              <SelectedIcon sx={{ position: 'absolute', right: -8, bottom: -14, fontSize: 90, color: 'rgba(255,255,255,.2)', transform: 'rotate(-12deg)' }} />
              <Box
                sx={{
                  position: 'relative', width: 48, height: 48, borderRadius: '16px', display: 'grid', placeItems: 'center', flexShrink: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,.42), rgba(255,255,255,.12))',
                  border: '1px solid rgba(255,255,255,.5)',
                }}
              >
                <SelectedIcon />
              </Box>
              <Box sx={{ position: 'relative', minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 900, fontSize: 18, lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,.25)' }} noWrap>
                  {name.trim() || 'Item name'}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.95 }} noWrap>
                  {selected.name}{quantity.trim() ? ` · ${quantity.trim()}` : ''}
                </Typography>
              </Box>
              {favourite && <StarRounded sx={{ position: 'relative', color: '#FDE68A' }} />}
            </Box>
          </Box>
        </Stack>
      </Box>
    </FormDialogShell>
  );
};

export default GroceryItemFormDialog;
